<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as DashboardStopDialogFormInitialData,
  ValidData as DashboardStopDialogFormValidData,
} from '@/components/dialogForms/DashboardStopDialogForm.vue';

import { computed, useCssModule, useTemplateRef } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import ThinJobStopDiagram from '@/components/diagrams/ThinJobStopDiagram.vue';
import DashboardStopDialogForm from '@/components/dialogForms/DashboardStopDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';
import useCurrentDateString from '@/composables/useCurrentDateString';

export interface Props {
  isConfirmLoading?: boolean;
  confirm?: (formData?: DashboardStopDialogFormValidData) => Promise<unknown>;
  driver?: number | string;
  jobId?: number | string;
  stopNum?: number;
  stopNames?: string[];
  isForm?: boolean;
  hasArrived?: boolean;
  isCompleted?: boolean;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  confirm: undefined,
  driver: undefined,
  jobId: undefined,
  stopNum: undefined,
  stopNames: () => [],
  isForm: false,
  hasArrived: false,
  isCompleted: false,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: Event | SubmitEventPromise | null | undefined,
    formData: DashboardStopDialogFormValidData | undefined,
    props: DefProps,
  ];
}>();

const styles = useCssModule();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: Event | SubmitEventPromise | null | undefined,
  formData?: DashboardStopDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  if (props.isForm) {
    const submitBtn = getSubmitBtn();
    if (!submitBtn) return;
    submitBtn.click();
  } else {
    handleSubmit(event);
  }
}

const { getCurrentDateString } = useCurrentDateString();

const initialData = computed<DashboardStopDialogFormInitialData>(() => {
  return {
    name: '',
    date: getCurrentDateString(),
    time: undefined,
  };
});

const width = computed(() => {
  let length = props.stopNames.length;
  if (!length || length < 2) {
    length = 2;
  }
  return parseInt(styles.modWidth) + (length * parseInt(styles.stopWidth) || 0);
});

const stopName = computed(() => {
  const stopIndex = (props.stopNum ?? 0) - 1;
  return props.stopNames[stopIndex];
});

const stops = computed(() => {
  if (props.isCompleted) {
    const length = props.stopNames.length;
    return props.stopNames.map((item, index) => {
      const isTruck = index + 1 === length;
      return {
        name: item,
        isTruckHere: isTruck,
      };
    });
  } else {
    return props.stopNames.map((item, index) => {
      const isTruck = index + 1 === props.stopNum;
      return {
        name: item,
        isTruckHere: isTruck,
        isTruckBefore: props.hasArrived,
      };
    });
  }
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.arrivedDepartedStopDialog"
    data-testid="arrivedDepartedStopDialog"
    :style="`width: ${width}px`"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        <template v-if="props.isCompleted">Job Completion</template>
        <template v-else>Stop {{ props.stopNum }}</template>
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <div :class="$style.text">
        The driver
        <b v-if="props.driver">#{{ props.driver }}</b>
        with a job
        <b v-if="props.jobId">#{{ props.jobId }}</b>
        <template v-if="props.isCompleted">
          has completed <b>all the stops</b>.
        </template>
        <template v-else>
          <template v-if="props.hasArrived"> has arrived to</template>
          <template v-else> has departed from</template>
          <b class="whitespace-nowrap">
            Stop<template v-if="props.stopNum">
              {{ ' ' }}
              {{ props.stopNum }}
            </template>
          </b>
          <template v-if="stopName">
            {{ ' ' }}
            <b class="whitespace-nowrap">"{{ stopName }}"</b>.
          </template>
          <template v-else>.</template>
        </template>
        <br />
        Is it correct? Please, confirm the action.
      </div>
      <ThinJobStopDiagram :class="$style.diagram" :stops="stops" />
      <DashboardStopDialogForm
        v-if="props.isForm"
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-check"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            :disabled="!props.confirm"
            @click="handleConfirmClick"
          >
            Confirm
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  modWidth: (variables.$pageDialogWindowPaddingX * 2) + variables.$thinJobStopDiagramPadding;
  stopWidth: variables.$thinJobStopDiagramStopWidth;
}

.arrivedDepartedStopDialog {
}
  .main {
  }
  .text {
    b {
      font-weight: 500;
    }
  }
  .diagram {
    margin: 20px 0;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
